import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  applyTheme, saveTheme, loadTheme, applyDensity, saveDensity, loadDensity,
  useShowCompleted, useIconFollowsTheme, useIconMark, useDefaultTab, useBoardGrouping, useDueSoon, useRounding, useTimerNudge, useWeeklyReview, useActivities, useTodayLogged, useNavOrder, inNavOrder,
  type DefaultTab, type BoardGrouping, type DueSoon,
} from "@/lib/theme";
import { THEME_META, type Mode, type ThemeName, type Density } from "@/lib/types";
import { card } from "@/components/atoms";
import { appMode, pb } from "@/lib/pocketbase";
import { useCalendarFeedUrl } from "@/lib/calendarFeed";
import { useBusiness } from "@/lib/business";
import { CopyableUrl, SubscribeHint } from "@/components/CalendarFeedLink";
import { CalendarSettingsPanel } from "@/components/CalendarSettingsPanel";
import { resetDemo } from "@/lib/mockPb";
import { useChrome } from "@/context/ChromeContext";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { WorkBlockAuto, WorkBlockConnection, WorkBlockLastRun } from "@/components/WorkBlockStatus";
import { TodoistStatus, TodoistSync } from "@/components/TodoistStatus";
import { DateCheck } from "@/components/DateCheck";
import { CleanupCard } from "@/components/CleanupCard";
import { useIsMobile } from "@/lib/useMediaQuery";
import { TRAVEL_DEFAULTS, useTravel, type TravelSettings } from "@/lib/travel";
import { CategorySettings, MileageRateSettings } from "@/views/expenses/ExpenseSettings";
import { HeaderTabs } from "@/components/HeaderTabs";
import { HelpGuides } from "@/views/settings/HelpGuides";
import { NAV } from "@/components/TopNav";
import { AppIcon } from "@/components/icons";
import { ICON_MARKS, type IconMark } from "@/lib/appIcons";

const MODES: [Mode, string][] = [["light", "Light"], ["dark", "Dark"], ["auto", "Automatic"]];
const DENSITIES: [Density, string][] = [["comfortable", "Comfortable"], ["compact", "Compact"]];
const TABS: [DefaultTab, string][] = [
  ["Board", "Board"], ["Grid", "Grid"], ["Timeline", "Timeline"],
  ["Calendar", "Calendar"], ["Notes", "Notes"], ["Overview", "Overview"],
];
const GROUPINGS: [BoardGrouping, string][] = [["phase", "Sections"], ["status", "Status"]];
const DUE_SOON: [DueSoon, string][] = [["1", "1 day"], ["3", "3 days"], ["7", "7 days"]];

type SettingsTab = "general" | "time" | "calendars" | "expenses" | "connections" | "business" | "account" | "help";
const SETTINGS_TABS: [SettingsTab, string][] = [
  ["general", "General"], ["time", "Time tracking"], ["calendars", "Calendars"], ["expenses", "Expenses"],
  ["connections", "Connections"], ["business", "Business"], ["account", "Account"], ["help", "Help"],
];
/** Deep links into a section open the tab that holds it. */
const TAB_OF_ANCHOR: Record<string, SettingsTab> = {
  calendars: "calendars", "expense-categories": "expenses", "mileage-rates": "expenses", "calendar-subscription": "calendars",
};
const TAB_KEY = "ph.settingsTab";


export function Settings() {
  const initial = loadTheme();
  const [theme, setTheme] = useState<ThemeName>(initial.theme);
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [density, setDensity] = useState<Density>(loadDensity());
  const [showCompleted, setShowCompleted] = useShowCompleted();
  const [iconFollows, setIconFollows] = useIconFollowsTheme();
  const [iconMark, setIconMark] = useIconMark();
  const [defaultTab, setDefaultTab] = useDefaultTab();
  const [boardGrouping, setBoardGrouping] = useBoardGrouping();
  const [dueSoon, setDueSoon] = useDueSoon();
  const [rounding, setRounding] = useRounding();
  const [timerNudge, setTimerNudge] = useTimerNudge();
  const [weeklyReview, setWeeklyReview] = useWeeklyReview();
  const [activities, setActivities] = useActivities();
  const [todayLogged, setTodayLogged] = useTodayLogged();
  const [navOrder, setNavOrder] = useNavOrder();
  const [business, setBusiness, businessOnServer] = useBusiness();
  const { data: feed } = useCalendarFeedUrl();
  const mobile = useIsMobile();
  const { user, logout } = useAuth();
  const toast = useToast();
  const feedUrl = feed?.url ?? null;

  function change(nextTheme: ThemeName, nextMode: Mode) {
    setTheme(nextTheme);
    setMode(nextMode);
    applyTheme(nextTheme, nextMode); // immediate, same frame
    saveTheme(nextTheme, nextMode);

    // Also onto the account. localStorage is per-browser and Safari evicts it
    // after about a week without a visit, so it alone reads as "the setting
    // didn't save". The local copy has already applied, but a failed save is
    // still worth saying out loud: swallowing it is how a schema that rejected
    // every new colour name went unnoticed.
    const userId = (pb.authStore.model as { id?: string } | null)?.id;
    if (appMode !== "demo" && userId) {
      void pb.collection("users").update(userId, { theme: nextTheme, mode: nextMode })
        .catch(() => toast("Couldn't save the colour to your account — other devices won't see it"));
    }
  }

  function changeDensity(next: Density) {
    setDensity(next);
    applyDensity(next);
    saveDensity(next);
  }

  // Expenses are managed on the phone by the Reimburse app, so the tab is desk-only.
  const tabs = SETTINGS_TABS.filter(([k]) => !(mobile && k === "expenses"));
  const { hash } = useLocation();
  const [picked, setPicked] = useState<SettingsTab>(() => {
    const fromLink = TAB_OF_ANCHOR[hash.slice(1)];
    if (fromLink) return fromLink;
    try { return (localStorage.getItem(TAB_KEY) as SettingsTab) || "general"; } catch { return "general"; }
  });
  const tab: SettingsTab = tabs.some(([k]) => k === picked) ? picked : "general";
  function pick(next: SettingsTab) {
    setPicked(next);
    try { localStorage.setItem(TAB_KEY, next); } catch { /* private mode */ }
  }
  // A link to a section switches to its tab; the effect below then scrolls to it.
  useEffect(() => {
    const t = TAB_OF_ANCHOR[hash.slice(1)];
    if (t) setPicked(t);
  }, [hash]);

  useChrome({
    title: "Settings",
    subtitle: "Workspace & appearance",
    tabs: mobile ? undefined : <HeaderTabs value={tab} options={tabs} onChange={pick} />,
  }, [tab, mobile]);

  // "Connect" on the Today and Time screens lands here; take it straight to Calendars.
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div style={{
      padding: mobile ? "14px 14px calc(var(--above-tabbar) + 88px)" : "20px 36px 90px",
      maxWidth: 760,
    }}>
      {/* The phone's header has no tab row, so the tabs sit on the page there. */}
      {mobile && (
        <div style={{ margin: "0 -14px 4px", padding: "0 14px", overflowX: "auto" }}>
          <HeaderTabs value={tab} options={tabs} onChange={pick} />
        </div>
      )}

      {tab === "general" && (
        <>
      <Group title="Appearance">
        {/* §11 lists one "Theme (Light/Dark)" row; this build has seven hues AND
            a light/dark mode, so they are two rows rather than one. */}
        <Row label="Theme" hint={THEME_META.find((t) => t.key === theme)?.label}>
          {/* Wraps: seven chips in one line overflow a phone, and because the
              Row's slot does not shrink that overflow scrolled the whole page
              sideways rather than clipping. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {THEME_META.map((t) => {
              const on = t.key === theme;
              return (
                <button
                  key={t.key}
                  onClick={() => change(t.key, mode)}
                  title={`${t.label} — ${t.hint}`}
                  aria-pressed={on}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    padding: "6px 12px 6px 8px", borderRadius: 999, cursor: "pointer",
                    border: on ? "1px solid var(--accentLine)" : "1px solid var(--line2)",
                    background: on ? "var(--accentTint2)" : "var(--surface)",
                    color: on ? "var(--accent)" : "var(--ink3)",
                    fontSize: 12.5, fontWeight: on ? 500 : 400,
                  }}
                >
                  <span className={`swatch-${t.key}`} style={{ width: 14, height: 14, borderRadius: "50%", flex: "none", border: "1px solid var(--line2)" }} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </Row>

        {/* A fixed hint, not one naming the pick: the Row wraps its control below
            the label when they don't fit side by side, and a hint whose width
            changed with each design made the tiles jump between the two. */}
        <Row label="App icon" hint="For the home screen, Dock and browser tab.">
          <AppIconPicker value={iconMark} onChange={setIconMark} themed={iconFollows} compact={mobile} />
        </Row>

        <Row
          label="App icon matches theme"
          hint={iconFollows
            ? "Adding to the home screen or Dock saves an icon in this colour. Already added? Remove it and add it again."
            : "Adding to the home screen or Dock saves the icon in the standard blue."}
        >
          <Switch on={iconFollows} onChange={setIconFollows} label="App icon matches theme" />
        </Row>

        <Row label="Light or dark" hint={mode === "auto" ? "Follows your system." : `Always ${mode}.`}>
          <Segmented value={mode} options={MODES} onChange={(m) => change(theme, m)} />
        </Row>

        <Row label="Row density" hint={density === "compact" ? "Tighter rows and cards." : "Roomier rows and cards."}>
          <Segmented value={density} options={DENSITIES} onChange={changeDensity} />
        </Row>

      </Group>

      <Group title="General">
        <Row label="Tab order" hint="The order of the tabs along the top bar. On this browser only.">
          <TabOrderEditor order={navOrder} onChange={setNavOrder} />
        </Row>
        <Row label="Default project tab" hint="Which tab a project opens on.">
          <Select value={defaultTab} options={TABS} onChange={setDefaultTab} />
        </Row>
        <Row label="Default board grouping" hint="Sections or status columns.">
          <Segmented value={boardGrouping} options={GROUPINGS} onChange={setBoardGrouping} />
        </Row>
        <Row label="Completed items" hint={showCompleted ? "Shown on the board and Grid." : "Hidden on the board and Grid."}>
          <Switch on={showCompleted} onChange={setShowCompleted} label="Completed items" />
        </Row>
        <Row label="“Due soon” means" hint="How far ahead a date counts as approaching.">
          <Select value={dueSoon} options={DUE_SOON} onChange={setDueSoon} />
        </Row>
        <Row
          label="Weekly review"
          hint={weeklyReview === "on"
            ? "A tab in Reports that walks the week in five steps: time, unbilled work, budgets, loose ends, next week."
            : "Hidden. Nothing is lost — turning it back on restores your ticks."}
        >
          <Switch on={weeklyReview === "on"} onChange={(v) => setWeeklyReview(v ? "on" : "off")} label="Weekly review" />
        </Row>
        <Row label="Week starts on" planned>
          <Select value="mon" options={[["mon", "Monday"], ["sun", "Sunday"]]} onChange={() => {}} disabled />
        </Row>
        <Row label="Show weekends" planned>
          <Switch on={false} onChange={() => {}} disabled label="Show weekends" />
        </Row>
        <Row label="Carry overdue forward" planned>
          <Switch on={false} onChange={() => {}} disabled label="Carry overdue forward" />
        </Row>
      </Group>
        </>
      )}

      {tab === "time" && (
      <Group title="Time tracking">
        <Row
          label="Activities"
          hint="Things you do often, offered as one tap in the capture bar. Picking one fills “What are you working on?” and is recorded on the entry, so it also becomes a breakdown in Reports."
        >
          <ActivityEditor list={activities} onChange={setActivities} />
        </Row>
        <Row
          label="Round durations"
          hint={
            rounding === "off"
              ? "Durations are stored exactly as logged. Turn this on to snap them to a billing increment — 6 min is tenths of an hour, 15 min is quarter-hour billing."
              : `Every duration snaps to the nearest ${rounding} minutes when it is saved — logged, timed or corrected. The running timer still ticks in real time, and anything shorter than one increment rounds up rather than away.`
          }
        >
          <Select
            value={rounding}
            options={[["off", "Exact"], ["5", "5 min"], ["6", "6 min"], ["10", "10 min"], ["15", "15 min"]]}
            onChange={setRounding}
          />
        </Row>
        <Row
          label="Travel rate"
          hint="Time logged with this activity bills at this share of the normal rate — pick it in the capture bar, quick log, timer or on the phone. Saved to your account, so every device agrees. Changing it affects new entries only."
        >
          <TravelField />
        </Row>
        <Row
          label="Long-running timer"
          hint={
            timerNudge === "off"
              ? "No reminder, however long a timer runs."
              : `A reminder when a timer passes ${timerNudge}h, and again at every ${timerNudge}h after — so one left running overnight is caught in the morning.`
          }
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <NotifyButton />
            <Select
              value={timerNudge}
              options={[["off", "Off"], ["1", "After 1h"], ["2", "After 2h"], ["3", "After 3h"], ["4", "After 4h"], ["6", "After 6h"]]}
              onChange={setTimerNudge}
            />
          </div>
        </Row>
        <Row
          label="Logged time on Today"
          hint={todayLogged === "on"
            ? "A Logged section above the day's tasks: the total, every entry (click one to edit it), and meetings that are over with nothing logged, one click from an entry. The total also shows in the header."
            : "Hidden. Today shows only your calendar and tasks."}
        >
          <Switch on={todayLogged === "on"} onChange={(v) => setTodayLogged(v ? "on" : "off")} label="Logged time on Today" />
        </Row>
        {/* The calendars themselves moved to their own tab once they became
            the Today screen as well; this points the way. */}
        <Row
          label="Meeting suggestions"
          hint="Recent meetings show on Time › Today as one-click entries. They come from the calendars with Suggest in Time turned on."
        >
          <button onClick={() => pick("calendars")} style={outlineBtn}>Calendars ›</button>
        </Row>
      </Group>
      )}

      {tab === "calendars" && (
        <>
      {/* Calendars read IN: the Today grid, and Time's meeting suggestions.
          Kept as an anchor: "Connect" on Today and Time links straight here. */}
      <Group title="Your calendars" id="calendars">
        <CalendarSettingsPanel />
      </Group>

      {/* Project Hub's own deadlines published OUT, for a calendar app to
          subscribe to — the opposite direction, so its own card. */}
      <Group title="Project Hub's calendar feed" id="calendar-subscription">
        <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5 }}>
          The other direction: your Project Hub due dates as a calendar, to subscribe to from Google, Outlook or Apple Calendar.
        </div>
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--tint2)" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: feedUrl ? "var(--ink)" : "var(--muted)" }}>
            Every project
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3, marginBottom: feedUrl ? 10 : 0 }}>
            {feedUrl
              ? "A read-only feed of every item with a due date. Individual projects have their own link on Overview › Definition."
              : appMode === "demo"
                ? "Only available on the live site."
                : feed?.reason
                  ? `Unavailable — ${feed.reason}`
                  : "Not set up yet — see below."}
          </div>
          {feedUrl && <><CopyableUrl url={feedUrl} /><SubscribeHint /></>}
        </div>
        <FeedSetupHelp open={!feedUrl && appMode === "live"} />
      </Group>
        </>
      )}

      {tab === "expenses" && (
        <>
      {/* Shared with the Reimburse phone app. Desk only: the phone app has its
          own screens for both. */}
      <Group title="Expenses">
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Shared with the Reimburse app, so a change here shows up on your phone too.</div>
        <SubGroup title="Categories" id="expense-categories">
          <CategorySettings />
        </SubGroup>
        <SubGroup title="Mileage rates" id="mileage-rates">
          <MileageRateSettings />
        </SubGroup>
      </Group>
        </>
      )}

      {tab === "connections" && (
        <>
      <Group title="Todoist">
        <Row
          label="Connection"
          hint={
            appMode === "demo"
              ? "Demo mode uses sample data — nothing is read from Todoist."
              : "Checks that the Worker's TODOIST_TOKEN reaches your Todoist account."
          }
        >
          <TodoistStatus />
        </Row>
        <Row
          label="Mirror"
          hint="Pulls your Todoist projects, sections and tasks into Project Hub. Todoist stays the source of truth — this never writes back."
        >
          <TodoistSync />
        </Row>
        <Row
          label="Dates"
          hint="Counts how many Todoist tasks actually carry a date and a deadline, and how many mirrored items here do. If this side shows more deadlines than Todoist sends, they are left over from an older sync and one more mirror run clears them."
        >
          <DateCheck />
        </Row>
        <Row
          label="Stale work"
          hint="Finished tasks Todoist no longer has. The sync keeps them on purpose — a completion that has aged out of Todoist's window looks the same as a deleted one, and guessing wrong loses history."
        >
          <CleanupCard />
        </Row>
      </Group>
      <Group title="Work blocks" id="work-blocks">
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          The Apps Script that reads your calendars and writes the blocks planned on Planner › Week ahead.
        </div>
        <Row label="Connection" hint="Reads next week's calendars through the script without writing anything.">
          <WorkBlockConnection />
        </Row>
        <Row label="Last run" hint="Written after every Send to Google and every automatic run.">
          <WorkBlockLastRun />
        </Row>
        <Row label="Automatic planning" hint="The first time you open Project Hub after this, it plans the coming week.">
          <WorkBlockAuto />
        </Row>
      </Group>
        </>
      )}

      {tab === "business" && (
        <>
      <Group title="Business details">
        {/* The letterhead on every printed report (components/ReportMasthead).
            Saved to your account (lib/business), so every device and
            Reimburse see the same details. */}
        <Row label="Logo" hint="Top-left of every printed report: client report, timesheet and expense package. With no logo, the business name goes there instead. At most 200 KB.">
          <LogoField value={business.logo} onChange={(logo) => setBusiness({ logo })} />
        </Row>
        <Row label="Business name" hint="Printed on every report: in place of the logo when there is none, otherwise beside your contact details. Reimburse uses it as the “Submitted by” name.">
          <Field value={business.name} onChange={(v) => setBusiness({ name: v })} placeholder="Vandehey Strategies" />
        </Row>
        <Row label="Email" hint="Shown in the header line.">
          <Field value={business.email} onChange={(v) => setBusiness({ email: v })} placeholder="you@example.com" />
        </Row>
        <Row label="Address" hint="One line.">
          <Field value={business.address} onChange={(v) => setBusiness({ address: v })} placeholder="City, State" wide />
        </Row>
        <Row label="Tax ID" hint="Optional — appended to the header line when set.">
          <Field value={business.taxId} onChange={(v) => setBusiness({ taxId: v })} placeholder="EIN / VAT" />
        </Row>
        <Row label="Footer note" hint="Payment terms or remittance details, printed under the table.">
          <Field value={business.notes} onChange={(v) => setBusiness({ notes: v })} placeholder="Payable within 30 days." wide />
        </Row>
        {businessOnServer === false && (
          <div style={{ fontSize: 12.5, color: "var(--warn)", marginTop: 10, lineHeight: 1.5 }}>
            Saved in this browser only — your PocketBase <code>users</code> collection has no <code>business</code> field,
            so the account did not keep these and your other devices and Reimburse won't see them. Add a JSON field
            named <code>business</code> to <code>users</code> in the PocketBase admin, then edit any field here to save again.
          </div>
        )}
      </Group>

      <Group title="Client reporting" planned>
        <Row label="Hide internal items" muted>
          <Switch on={true} onChange={() => {}} disabled label="Hide internal items" />
        </Row>
        <Row label="Include firm logo" muted>
          <Switch on={false} onChange={() => {}} disabled label="Include firm logo" />
        </Row>
        <Row label="Report footer" muted>
          <button disabled style={{ ...outlineBtn, color: "var(--muted3)", cursor: "not-allowed" }}>Edit</button>
        </Row>
      </Group>
        </>
      )}

      {tab === "account" && (
        <>
      <Group title="Notifications" planned>
        <Row label="Email digest" muted>
          <Select value="off" options={[["off", "Off"], ["daily", "Daily"], ["weekly", "Weekly"]]} onChange={() => {}} disabled />
        </Row>
        <Row label="Alert when work is blocked" muted>
          <Switch on={false} onChange={() => {}} disabled label="Alert when work is blocked" />
        </Row>
      </Group>

      <Group title="Data">
        <Row label="Export project data" hint="Until then, each project has its own CSV export on Overview › Definition." planned>
          <button disabled style={{ ...outlineBtn, color: "var(--muted3)", cursor: "not-allowed" }}>Export CSV</button>
        </Row>
        <Row
          label="Reset demo data"
          hint={appMode === "demo" ? "Restores the sample projects in this browser." : "Only available in demo mode."}
          muted={appMode !== "demo"}
        >
          <button
            disabled={appMode !== "demo"}
            onClick={resetDemo}
            style={{ ...outlineBtn, color: appMode === "demo" ? "var(--bad)" : "var(--muted3)", cursor: appMode === "demo" ? "pointer" : "not-allowed" }}
          >
            Reset
          </button>
        </Row>
      </Group>

      <Group title="Account">
        <Row
          label="Signed in as"
          hint={appMode === "demo" ? "The demo's sample account — nothing here reaches a server." : user?.name || undefined}
        >
          <span style={{ fontSize: 13, color: "var(--ink2)", overflowWrap: "anywhere", textAlign: "right" }}>
            {user?.email || "Not signed in"}
          </span>
        </Row>
        <Row
          label="Sign out"
          hint={appMode === "demo" ? "Leaves the demo." : "Ends this session on this device."}
        >
          {/* Sign out used to be a power icon in the sidebar footer alongside
              two other icons; the footer is now one control, so it lives here. */}
          <button onClick={logout} style={{ ...outlineBtn, color: "var(--bad)" }}>Sign out</button>
        </Row>
      </Group>
        </>
      )}

      {tab === "help" && (
      <Group title="Guides">
        <div style={{ fontSize: 12, color: "var(--muted)" }}>Step-by-step how-tos for Project Hub. Click a guide to open it.</div>
        <HelpGuides />
      </Group>
      )}
    </div>
  );
}

// ---- Row anatomy (§11) -----------------------------------------------------
// Label at 13.5/500 over a --muted hint at 12, control right-aligned.

/**
 * The activity list, edited in place.
 *
 * A chip each with a ✕, and one field to add. No ordering control: the list is
 * sorted for you, because a hand-ordered list of seven words is a thing to
 * maintain rather than a thing to use.
 */
/**
 * How to turn the calendar feed on. Everything it needs lives on the Worker,
 * so this is a checklist for the Cloudflare dashboard rather than a form.
 * Open by default while the feed is off on the live site; collapsed once on.
 */
function FeedSetupHelp({ open }: { open: boolean }) {
  return (
    <details open={open} style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 14 }}>
      <summary style={{ cursor: "pointer", color: "var(--ink2)" }}>How do I set up the calendar subscription?</summary>
      <ol style={{ margin: "8px 0 0", paddingLeft: 18, lineHeight: 1.6 }}>
        <li>
          <b>Make a long random string.</b> It becomes part of the feed's address and works as its password.
          In Terminal, <code>openssl rand -hex 24</code> makes one, or use a password manager's generator
          (30+ characters, letters and numbers only).
        </li>
        <li>
          <b>Add three secrets to the Worker.</b> Cloudflare dashboard → Workers &amp; Pages → <b>project-hub</b> →
          Settings → Variables and Secrets → Add, choosing type <i>Secret</i> for each:
          <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
            <li><code>ICS_TOKEN</code>: the random string from step 1</li>
            <li><code>FEED_EMAIL</code>: the email you sign in to Project Hub with</li>
            <li><code>FEED_PASSWORD</code>: that account's password. The feed signs in as you to read your projects, because a calendar app can't sign in by itself.</li>
          </ul>
          The names must match exactly, in capitals. Deploy when the dashboard asks.
        </li>
        <li>
          <b>Reload this page</b> while signed in as that same account. The feed's address appears above, with a copy
          button. Anyone signed in as a different account sees no link.
        </li>
        <li>
          <b>Subscribe in your calendar app.</b> Google Calendar: Other calendars → + → From URL. Apple Calendar:
          File → New Calendar Subscription. Outlook: Add calendar → Subscribe from web. Each project also has its
          own link on Overview › Definition, for a separate calendar per project.
        </li>
      </ol>
      <p style={{ margin: "8px 0 0" }}>
        Calendar apps refresh subscriptions on their own schedule, from minutes to about a day for Google. Treat the
        address like a password: changing <code>ICS_TOKEN</code> cuts off every existing subscription at once.
      </p>
    </details>
  );
}

/**
 * The top bar's tabs as a list with up and down on each row. Saved as the full
 * order every time, so a later change to the built-in order never shuffles one
 * you set; Reset goes back to following the built-in order.
 */
function TabOrderEditor({ order, onChange }: { order: string; onChange: (next: string) => void }) {
  const tabs = inNavOrder(NAV, order);

  function move(i: number, by: number) {
    const next = tabs.map((t) => t.to);
    [next[i], next[i + by]] = [next[i + by], next[i]];
    onChange(next.join(","));
  }

  const arrow = (disabled: boolean): React.CSSProperties => ({
    width: 26, height: 26, borderRadius: 6, border: "1px solid var(--line2)", background: "var(--surface)",
    color: disabled ? "var(--muted3)" : "var(--ink2)", cursor: disabled ? "default" : "pointer", fontSize: 12, lineHeight: 1,
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
      <ol style={{ listStyle: "none", margin: 0, padding: 0, width: 220, border: "1px solid var(--line2)", borderRadius: 10, overflow: "hidden" }}>
        {tabs.map((t, i) => (
          <li key={t.to} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "6px 8px 6px 12px",
            borderTop: i ? "1px solid var(--line)" : "none", background: "var(--surface)",
            fontSize: 12.5, color: "var(--ink2)",
          }}>
            <span style={{ display: "inline-flex", color: "var(--muted)" }}><t.Icon size={16} /></span>
            <span style={{ flex: 1 }}>{t.label}</span>
            <button onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move ${t.label} left`} style={arrow(i === 0)}>↑</button>
            <button onClick={() => move(i, 1)} disabled={i === tabs.length - 1} aria-label={`Move ${t.label} right`}
              style={arrow(i === tabs.length - 1)}>↓</button>
          </li>
        ))}
      </ol>
      {order && <button onClick={() => onChange("")} style={outlineBtn}>Reset to default</button>}
    </div>
  );
}

function ActivityEditor({ list, onChange }: { list: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState("");

  function add() {
    const value = draft.trim();
    if (!value) return;
    onChange([...list, value]);
    setDraft("");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, maxWidth: 420 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
        {list.length === 0 && (
          <span style={{ fontSize: 12.5, color: "var(--muted2)" }}>None — the picker stays hidden.</span>
        )}
        {list.map((a) => (
          <span key={a} style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 6px 4px 11px",
            borderRadius: 999, border: "1px solid var(--line2)", background: "var(--surface)",
            fontSize: 12.5, color: "var(--ink2)",
          }}>
            {a}
            <button
              onClick={() => onChange(list.filter((x) => x !== a))}
              aria-label={`Remove ${a}`}
              style={{ border: "none", background: "none", color: "var(--muted3)", cursor: "pointer", fontSize: 12, padding: "0 2px" }}
            >✕</button>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add an activity"
          aria-label="Add an activity"
          style={{
            width: 170, padding: "7px 10px", borderRadius: 8, fontSize: 12.5,
            border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
          }}
        />
        <button
          onClick={add}
          style={{
            padding: "7px 13px", borderRadius: 8, border: "1px solid var(--line2)",
            background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
          }}
        >Add</button>
      </div>
    </div>
  );
}

/**
 * Logo upload, inlined as a data URL.
 *
 * 200 KB is a deliberate ceiling: localStorage is a few megabytes total and
 * shared with every other preference, so a 4 MB photo dropped in here would
 * silently break saving everything else.
 */
function LogoField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const toast = useToast();

  function pick(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast("Pick an image file"); return; }
    if (file.size > 200_000) { toast("That image is over 200 KB — use a smaller one"); return; }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.onerror = () => toast("Could not read that file");
    reader.readAsDataURL(file);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {value && (
        <img
          src={value}
          alt="Your logo"
          style={{ height: 32, maxWidth: 140, objectFit: "contain", borderRadius: 4, background: "var(--surface2)", padding: 3 }}
        />
      )}
      <label style={{
        padding: "7px 13px", borderRadius: 8, cursor: "pointer", fontSize: 12.5,
        border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
      }}>
        {value ? "Replace" : "Upload"}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
          style={{ display: "none" }}
        />
      </label>
      {value && (
        <button
          onClick={() => onChange("")}
          style={{
            padding: "7px 11px", borderRadius: 8, cursor: "pointer", fontSize: 12.5,
            border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--bad)",
          }}
        >Remove</button>
      )}
    </div>
  );
}

/** Percent and activity, saved to the account when a field is left. */
function TravelField() {
  const [travel, saveTravel] = useTravel();
  const toast = useToast();
  const [pct, setPct] = useState(String(travel.pct));
  const [activity, setActivity] = useState(travel.activity);

  async function commit(next: TravelSettings) {
    if (next.pct === travel.pct && next.activity === travel.activity) return;
    try {
      await saveTravel(next);
      toast(`Travel bills at ${next.pct}%`);
    } catch {
      toast("Couldn't save the travel rate to your account");
    }
  }
  function commitPct() {
    const n = Math.round(Number(pct));
    if (!Number.isFinite(n) || n < 1 || n > 100) { setPct(String(travel.pct)); toast("Enter a percent from 1 to 100"); return; }
    void commit({ pct: n, activity: activity.trim() || TRAVEL_DEFAULTS.activity });
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input
          value={pct}
          inputMode="numeric"
          onChange={(e) => setPct(e.target.value.replace(/[^\d]/g, ""))}
          onBlur={commitPct}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          aria-label="Travel rate percent"
          style={{ width: 56, padding: "7px 8px", borderRadius: 8, fontSize: 13, textAlign: "right", border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)" }}
        />
        <span style={{ fontSize: 12.5, color: "var(--ink3)" }}>% for</span>
      </span>
      <input
        value={activity}
        onChange={(e) => setActivity(e.target.value)}
        onBlur={() => void commit({ pct: travel.pct, activity: activity.trim() || TRAVEL_DEFAULTS.activity })}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        aria-label="Travel activity"
        style={{ width: 110, padding: "7px 10px", borderRadius: 8, fontSize: 13, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)" }}
      />
    </div>
  );
}

/**
 * Lets the timer reminder reach you when Project Hub is not the tab in front.
 * Browsers only allow the permission prompt from a click, so it is a button,
 * and it disappears once the answer is in.
 */
function NotifyButton() {
  const supported = typeof Notification !== "undefined";
  const [perm, setPerm] = useState(supported ? Notification.permission : "denied");
  if (!supported || perm === "granted") return null;
  if (perm === "denied") {
    return <span style={{ fontSize: 11.5, color: "var(--muted)" }}>Notifications blocked — in-app only</span>;
  }
  return (
    <button
      onClick={() => void Notification.requestPermission().then(setPerm)}
      style={outlineBtn}
      title="Also notify when this tab is in the background"
    >Allow notifications</button>
  );
}

function Field({ value, onChange, placeholder, wide }: {
  value: string; onChange: (v: string) => void; placeholder?: string; wide?: boolean;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      style={{
        width: wide ? 320 : 220, maxWidth: "100%", padding: "8px 11px", borderRadius: 8, fontSize: 13,
        border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
      }}
    />
  );
}

/**
 * `planned` marks a whole section that is not built yet. Its rows are shown so
 * you can see what is coming, but nothing in it does anything today.
 */
function Group({ title, children, id, planned }: { title: string; children: React.ReactNode; id?: string; planned?: boolean }) {
  return (
    <div id={id} style={{ ...card, padding: 18, marginTop: 14, scrollMarginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)", color: planned ? "var(--muted)" : undefined }}>{title}</span>
        {planned && <PlannedTag />}
      </div>
      {planned && <div style={{ fontSize: 12, color: "var(--muted)" }}>Not built yet. These are here to show what's coming.</div>}
      {children}
    </div>
  );
}

/** A titled part of a Group, for a panel that belongs with the group's settings. */
function SubGroup({ title, children, id }: { title: string; children: React.ReactNode; id?: string }) {
  return (
    <div id={id} style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--tint2)", scrollMarginTop: 16 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{title}</div>
      {children}
    </div>
  );
}

function PlannedTag() {
  return (
    <span title="Not built yet" style={{
      fontSize: 10.5, fontWeight: 500, letterSpacing: "0.3px", textTransform: "uppercase",
      padding: "2px 7px", borderRadius: 999, background: "var(--tint)", color: "var(--muted)",
      border: "1px dashed var(--line2)", whiteSpace: "nowrap",
    }}>Planned</span>
  );
}

/** `planned`: a setting that is not built yet — greyed, tagged, and inert. */
function Row({ label, hint, children, muted, planned }: {
  label: string; hint?: string; children: React.ReactNode; muted?: boolean; planned?: boolean;
}) {
  muted = muted || planned;
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
      // Wraps rather than overflows: the theme picker is nine buttons wide and
      // does not fit beside its label on a phone.
      flexWrap: "wrap",
      marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--tint2)",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 500, color: muted ? "var(--muted)" : "var(--ink)" }}>
          {label}
          {planned && <PlannedTag />}
        </div>
        {hint && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>{hint}</div>}
      </div>
      <div style={{ flex: "none", maxWidth: "100%" }}>{children}</div>
    </div>
  );
}

/**
 * The six app icon designs, drawn in the colour the installed icon will be:
 * the theme's (the light-mode accent, which the PNGs use in either mode), or
 * the standard blue when the icon doesn't follow the theme.
 */
function AppIconPicker({ value, onChange, themed, compact }: {
  value: IconMark; onChange: (m: IconMark) => void; themed: boolean; compact?: boolean;
}) {
  // Smaller on a phone so all six fit on one line rather than leaving one
  // stranded on a second.
  const size = compact ? 34 : 40;
  const bg = themed ? "oklch(0.528 var(--c) var(--hue))" : "oklch(0.528 0.13 245)";
  return (
    <div role="radiogroup" aria-label="App icon" style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {ICON_MARKS.map((m) => {
        const on = m.key === value;
        return (
          <button
            key={m.key}
            role="radio"
            aria-checked={on}
            onClick={() => onChange(m.key)}
            title={m.label}
            style={{
              padding: compact ? 3 : 4, borderRadius: 12, cursor: "pointer", background: "transparent",
              border: on ? "2px solid var(--accentLine)" : "2px solid transparent",
            }}
          >
            <AppIcon mark={m.key} size={size} radius={size * 0.225} bg={bg} />
          </button>
        );
      })}
    </div>
  );
}

/** 38×20 switch. */
function Switch({ on, onChange, disabled, label }: {
  on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      style={{
        width: 38, height: 20, borderRadius: 999, border: "none", padding: 0, position: "relative",
        background: on ? (disabled ? "var(--muted3)" : "var(--accent)") : "var(--tint2)",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
        transition: "background 0.12s",
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: on ? 20 : 2, width: 16, height: 16, borderRadius: "50%",
        background: "var(--surface)", boxShadow: "var(--shadowCard)", transition: "left 0.12s",
      }} />
    </button>
  );
}

function Segmented<T extends string>({ value, options, onChange }: {
  value: T; options: [T, string][]; onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 3, padding: 3, background: "var(--tint)", borderRadius: 999 }}>
      {options.map(([v, label]) => {
        const on = v === value;
        return (
          <button key={v} onClick={() => onChange(v)} style={{
            padding: "6px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
            border: on ? "1px solid var(--accentLine)" : "1px solid transparent",
            background: on ? "var(--surface)" : "transparent",
            color: on ? "var(--accent)" : "var(--ink3)", fontWeight: on ? 500 : 400,
          }}>{label}</button>
        );
      })}
    </div>
  );
}

function Select<T extends string>({ value, options, onChange, disabled }: {
  value: T; options: [T, string][]; onChange: (v: T) => void; disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as T)}
      style={{
        padding: "7px 11px", borderRadius: 999, border: "1px solid var(--line2)",
        background: "var(--surface)", color: disabled ? "var(--muted3)" : "var(--ink2)",
        fontSize: 12.5, cursor: disabled ? "not-allowed" : "pointer", minWidth: 132,
      }}
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

const outlineBtn: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};

